import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";

import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput } from "components/Shared/Inputs";
import { ChooseYesNo } from "components/CriticalIncident/components/ChooseYesNo";
import { NOTIFIED_PARTY_MAX_LENGTH, NOTIFIED_PARTY_FIELD_MAX_LENGTH } from "../../RiskAssessments.constants";
import { useRiskAssessmentFieldActionsContext } from "../../hooks/RiskAssessmentFieldActionsContext";

export interface NotifiedPartyFieldsProps {
  partyIndex: number;
}

export const NotifiedPartyFields: FunctionComponent<NotifiedPartyFieldsProps> = ({ partyIndex }) => {
  const { t, i18n } = useTranslation("criticalIncident");
  const {
    basePath,
    record,
    recordTouched,
    recordErrors,
    isReadonly,
    makeNotifiedPartyChangeHandler,
    handleNotifiedPartyDateChange,
    handleNotifiedPartyBooleanChange,
  } = useRiskAssessmentFieldActionsContext();
  const party = (record.partiesNotified ?? [])[partyIndex];
  const partiesNotifiedTouched = recordTouched?.partiesNotified as unknown as Record<string, unknown>[] | undefined;
  const partiesNotifiedErrors = recordErrors?.partiesNotified as unknown as Record<string, string>[] | undefined;
  const partyTouched = partiesNotifiedTouched?.[partyIndex];
  const partyErrors = partiesNotifiedErrors?.[partyIndex];

  const fieldPath = (field: string) => `${basePath}.partiesNotified[${partyIndex}].${field}`;
  const partyValues = party as unknown as Record<string, string>;

  const dateField = (labelKey: string, fullWidth=false) => (
    <Grid item sm={fullWidth ? 12 : 6} key="notifiedDate">
      <FormControlWrapper isReadonly={isReadonly} type="date" dateFormat="L" required label={t(labelKey)} name={fieldPath("notifiedDate")} fullWidth>
        <DateInput
          label={t(labelKey)}
          name={fieldPath("notifiedDate")}
          onChange={handleNotifiedPartyDateChange(partyIndex)}
          value={partyValues?.notifiedDate ? moment(partyValues?.notifiedDate) : null}
          error={Boolean(partyTouched?.notifiedDate) && Boolean(partyErrors?.notifiedDate)}
          helperText={partyTouched?.notifiedDate ? t(partyErrors?.notifiedDate || " ") : ""}
        />
      </FormControlWrapper>
    </Grid>
  );

  const booleanField = (field: string, labelKey: string, fullWidth=true, required=true) => (
    <Grid item sm={fullWidth ? 12 : 6} key={field}>
      <FormControlWrapper
        isReadonly={isReadonly}
        type="radio.yesno"
        required={required}
        label={t(labelKey)}
        name={fieldPath(field)}
        error={Boolean(partyTouched?.[field]) && Boolean(partyErrors?.[field])}
        helperText={partyTouched?.[field] ? t(partyErrors?.[field] || " ") : ""}
      >
        <ChooseYesNo
          label={t(labelKey)}
          name={fieldPath(field)}
          required={required}
          value={(partyValues?.[field] as unknown as boolean | null) ?? null}
          onChange={handleNotifiedPartyBooleanChange(partyIndex, field)}
          error={Boolean(partyTouched?.[field]) && Boolean(partyErrors?.[field])}
          helperText={partyTouched?.[field] ? t(partyErrors?.[field] || " ") : ""}
        />
      </FormControlWrapper>
    </Grid>
  );

  const textField = (field: string, labelKey: string, fullWidth=false, maxLength=NOTIFIED_PARTY_FIELD_MAX_LENGTH) => (
    <Grid item sm={fullWidth ? 12 : 6} key={field}>
      <FormControlWrapper isReadonly={isReadonly} label={t(labelKey)} name={fieldPath(field)} fullWidth>
        <TextBox
          label={t(labelKey)}
          name={fieldPath(field)}
          value={partyValues?.[field] ?? ""}
          onChange={makeNotifiedPartyChangeHandler(partyIndex, field)}
          required
          slotProps={{ input: { maxLength } }}
          error={Boolean(partyTouched?.[field]) && Boolean(partyErrors?.[field])}
          helperText={partyTouched?.[field] ? t(partyErrors?.[field] || " ") : ""}
        />
      </FormControlWrapper>
    </Grid>
  );

  const notesField = (field: "notes" | "conversationDetails", labelKey: string, fullWidth=true, maxLength=NOTIFIED_PARTY_MAX_LENGTH) => (
    <Grid item sm={fullWidth ? 12 : 6} key={field}>
      <FormControlWrapper isReadonly={isReadonly} label={t(labelKey)} name={fieldPath(field)} fullWidth>
        <TextBox
          multiline
          minRows={2}
          maxRows={4}
          label={t(labelKey)}
          name={fieldPath(field)}
          value={partyValues?.[field] ?? ""}
          onChange={makeNotifiedPartyChangeHandler(partyIndex, field)}
          required
          slotProps={{ input: { maxLength } }}
          error={Boolean(partyTouched?.[field]) && Boolean(partyErrors?.[field])}
          helperText={partyTouched?.[field] ? t(partyErrors?.[field] || " ") : ""}
        />
      </FormControlWrapper>
    </Grid>
  );

  const fields = (() => {
    switch (party?.partyType) {
      case "clinicalSupervisor":
        return [
          booleanField("wereOthersInformed", "were_others_informed", true, false),
          ...(partyValues?.wereOthersInformed ? [dateField("clinical_supervisor_notified_date", true)] : []),
        ];
      case "potentialVictim":
        return [
          dateField("potential_victim_notified_date", i18n.language.startsWith("fr")),
          textField("victimName", "potential_victim_name", i18n.language.startsWith("fr")),
          textField("victimPhone", "potential_victim_phone", i18n.language.startsWith("fr")),
          notesField("notes", "potential_victim_notes"),
        ];
      case "police":
        return [
          dateField("police_notified_date"),
          textField("officerName", "police_officer_name"),
          textField("policePhone", "police_phone"),
          textField("badgeNumber", "police_badge_number"),
          notesField("conversationDetails", "police_conversation_details"),
        ];
      case "childWelfare":
        return [
          dateField("child_welfare_notified_date", i18n.language.startsWith("fr")),
          textField("agencyName", "child_welfare_agency_name", i18n.language.startsWith("fr")),
          textField("representativeName", "child_welfare_representative_name", i18n.language.startsWith("fr")),
          textField("referenceNumber", "child_welfare_reference_number", i18n.language.startsWith("fr")),
          notesField("conversationDetails", "child_welfare_conversation_details"),
        ];
      case "emergencyDepartment":
        return [
          dateField("emergency_department_referral_date"),
          textField("departmentName", "emergency_department_name"),
          notesField("notes", "emergency_department_notes"),
        ];
      case "otherAgency":
        return [
          textField("agencyName", "other_agency_name"),
          notesField("notes", "other_agency_notes"),
        ];
      case "family":
        return [
          dateField("family_informed_date"),
          textField("memberName", "family_member_name"),
          textField("relationshipToClient", "family_member_relationship_to_client", i18n.language.startsWith("fr")),
          textField("familyPhone", "family_phone"),
          notesField("conversationDetails", "family_conversation_details"),
        ];
      case "workplace":
        return [
          dateField("workplace_notified_date"),
          textField("contactName", "workplace_contact_name", i18n.language.startsWith("fr")),
          textField("jobTitle", "workplace_job_title"),
          textField("workplacePhone", "workplace_phone"),
          notesField("conversationDetails", "workplace_conversation_details"),
        ];
      case "learningInstitution":
        return [
          dateField("learning_institution_notified_date", i18n.language.startsWith("fr")),
          textField("contactName", "learning_institution_contact_name", i18n.language.startsWith("fr")),
          textField("institutionDepartment", "learning_institution_department", i18n.language.startsWith("fr")),
          textField("institutionPhone", "learning_institution_phone", i18n.language.startsWith("fr")),
          notesField("conversationDetails", "learning_institution_conversation_details"),
        ];
      case "none":
      default:
        return [];
    }
  })();

  return <Grid container spacing={2}>{fields}</Grid>;
};
